import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { Translate } from 'react-localize-redux';

import { SortableHandle } from 'react-sortable-hoc';

import Typography from '@components/basics/typography';
import HandleIcon from '@components/basics/icons/handle';
import DeleteIcon from '@components/basics/icons/delete';
import MotionOpacity from '@components/motion/motion-opacity';
import ProgressbarInline from '@components/basics/progressbar-inline';
import SquareErrorIcon from '@components/basics/icons/square-error-icon';
import SquareCheckIcon from '@components/basics/icons/square-check-icon';
import SquareProcessingIcon from '@components/basics/icons/square-processing-icon';

import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import TrackActionsDropdown from '@webapp/controllers/form/track-actions-dropdown';
import { getMenuOptions } from './menu-options';
import LoadingCtrl from '@webapp/controllers/loading';
import { SimpleRelease } from '@common/types/release/simple-release';

export interface UploadListItemProps {
  className?: string;
  number?: string;
  text: string;
  done?: boolean;
  active?: boolean;
  uploading?: boolean;
  progress?: number;
  error?: boolean;
  isClean?: boolean;
  isExplicit?: boolean;
  isInstrumental?: boolean;
  isLoading?: boolean;
  artwork?: React.ReactNode;
  assetId?: string;
  accept?: string;
  trackLength?: string;
  hasNFTSwitch?: boolean;

  isMobile: boolean;
  activeRelease?: SimpleRelease;

  onRetry: () => void;
  onRemove?: () => void;
  onReplace?: (files: FileList) => void;
  onCancel: (assetId?: string) => void;
  handleTrackMenu: (action: string) => void;

  disableRemove?: boolean;
  disableSorting?: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  activeRelease: state.release.release,
});

const Handle = SortableHandle((props: { className?: string }) => (
  <StyledHandleIcon {...props} />
));

export const UploadListItem: React.FC<UploadListItemProps> = (props) => {
  //comment this peidce of code to handle in ticket DEVTEAM1163
  // const [ NFT, setNFT] = React.useState<string>('');

  // const handleNFT = (value: string) => {
  //   if (false) {
  //     setNFT('');
  //   } else {
  //     setNFT(value);
  //   }
  // };

  return (
    <MotionOpacity>
      <div className={props.className}>
        {!props.disableSorting || !props.error ? (
          <Handle className="icon-handle" />
        ) : null}
        {props.artwork ? (
          <ArtworkWrapper>{props.artwork}</ArtworkWrapper>
        ) : props.uploading ? (
          <IconsWrapper>
            <SquareProcessingIcon />
          </IconsWrapper>
        ) : props.error ? (
          <IconsWrapper>
            <SquareErrorIcon />
          </IconsWrapper>
        ) : (
          <IconsWrapper>
            <SquareCheckIcon />
          </IconsWrapper>
        )}
        <div className="name">
          <Typography className="text" weight="700">
            {props.text.length > 20
              ? `${props.text.slice(0, 20)}...`
              : props.text}
          </Typography>
          <UploadStatusText
            status={
              props.uploading
                ? 'uploading'
                : props.done
                ? 'success'
                : props.error
                ? 'error'
                : ''
            }
            className="text"
            weight="500"
          >
            {props.uploading
              ? 'Uploading...'
              : props.done
              ? 'Upload Complete'
              : ''}
          </UploadStatusText>
        </div>
        <div className="action" onClick={(ev) => ev.stopPropagation()}>
          {!props.uploading &&
            !props.isLoading &&
            !props.error &&
            !props.hasNFTSwitch &&
            props.trackLength && (
              <TrackLength type="small">{props.trackLength}</TrackLength>
            )}
          {
            !props.uploading && !props.isLoading && !props.error
            // props.hasNFTSwitch && (
            //   <Switch
            //     isGreen
            //     activeText="NFT"
            //     inactiveText="NFT"
            //     active={NFT === props.text}
            //     onClick={() => handleNFT(props.text)}
            //   />
            // )
          }
          {props.uploading ? (
            <>
              {!props.isMobile ? (
                <StyledDeleteIcon
                  isRedIcon
                  noHover={props.isMobile}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    props.assetId
                      ? props.onCancel(props.assetId)
                      : props.onCancel();
                  }}
                />
              ) : null}
            </>
          ) : props.isLoading ? (
            <LoadingCtrl
              className="loader"
              loading={props.isLoading}
              size="48px"
            />
          ) : null}
          {!props.uploading ? (
            props.onRemove ? (
              <MenuButton>
                <TrackActionsDropdown
                  hasDottedIcon
                  trackId={props.assetId}
                  menuContent={getMenuOptions(
                    props.disableRemove,
                    !!props.activeRelease?.source
                  )}
                  title={props.text}
                  onSelect={(action: string) => props.handleTrackMenu(action)}
                />
              </MenuButton>
            ) : null
          ) : null}
          {!props.error ? (
            !props.uploading && props.onReplace ? (
              <CustomButton
                backgroundColor="grey"
                className={`action-text ${
                  props.isMobile ? 'show-on-mobile' : ''
                }`}
              >
                <Typography weight="700">
                  <Translate id="UPLOAD.BTN.TEXT.CHANGE" />
                </Typography>
                <input
                  type="file"
                  className="file"
                  accept={props.accept}
                  onChange={(ev) =>
                    ev.target.files?.length && props.onReplace
                      ? props.onReplace(ev.target.files)
                      : null
                  }
                />
              </CustomButton>
            ) : null
          ) : null}
        </div>
        {props.uploading || !props.isMobile ? (
          <ProgressbarInline
            className="progressline"
            isDone={!props.uploading && !props.error}
            progress={props.progress}
            isFullWidth
          />
        ) : null}
      </div>
      {!!props.activeRelease?.source && props.error ? (
        <ErrorWrapper>
          <Typography type="smaller" weight="700">
            {/* TODO: DEVTEAM-958 - The error should show what is wrong with the uploaded item */}
            <Translate id="UPLOAD.ERROR.INVALID_LENGTH" />
          </Typography>
        </ErrorWrapper>
      ) : null}
    </MotionOpacity>
  );
};

export default connect(mapStateToProps)(styled(UploadListItem)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  user-select: none;
  background-color: ${({ theme }) => theme.colors.differentBackground};
  padding: 16px 18px;

  &:last-child {
    margin-bottom: 0;
  }

  ${(props) =>
    !props.disableSorting
      ? css`
          &:hover {
            .number {
              opacity: 0;
            }
            @media ${({ theme }) => theme.device.tablet} {
              .icon-handle {
                opacity: 1;
              }
            }
          }
        `
      : ''}

  .number {
    margin-right: 16px;
    height: 32px;
    width: 32px;
    flex-basis: 32px;
    flex-shrink: 0;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  .loader {
    position: absolute;
    right: 34px;
    top: 50%;
    transform: translate(0, -50%);
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      right: 0;
    }
  }

  .name {
    width: calc(60% - 38px);
    ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
      width: calc(70% - 38px);
    }
  }

  .action {
    width: 40%;
    flex-grow: 0;
    flex-shrink: 0;
    text-align: right;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
      width: 30%;
    }

    .action-text {
      cursor: pointer;
      ${(props) =>
        props.isMobile &&
        css`
          &.show-on-mobile {
            opacity: 1;
            pointer-events: all;
            z-index: 999;
          }
        `}
    }

    .progressline {
      transition: opacity
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};
    }
  }

  ${(props) =>
    !props.error
      ? css`
          &:hover {
            .action {
              .progressline {
                opacity: 0;
              }
            }
            .loader {
              opacity: 0;
              pointer-events: none;
            }
          }
        `
      : css`
          .action {
            .action-text {
              opacity: 1;
              pointer-events: all;
              z-index: 999;
            }
          }
        `}

  &:hover {
    .action {
      .action-text {
        opacity: 1;
        pointer-events: all;
        z-index: 999;
      }
    }
  }
`);

const CustomButton = styled.div<{ backgroundColor?: 'red' | 'grey' }>`
  border-radius: 90px;
  height: 32px;
  padding: 0 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

  ${({ backgroundColor }) =>
    backgroundColor === 'red'
      ? css`
          background-color: ${({ theme }) => rgba(theme.colors.accent, 0.07)};

          &:hover {
            background-color: ${({ theme }) => rgba(theme.colors.accent, 0.15)};
          }
        `
      : backgroundColor === 'grey'
      ? css`
          background-color: ${({ theme }) => theme.colors.iconBackground};

          &:hover {
            background-color: ${({ theme }) =>
              theme.colors.iconBackgroundHover};
          }
        `
      : null}

  .file {
    cursor: pointer;
    position: absolute;
    width: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  }
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  margin-left: 8px;
  width: 24px;
  height: 24px;
`;

const IconsWrapper = styled.div`
  margin-right: 10px;
  max-width: 28px;
  max-height: 28px;
`;

const ArtworkWrapper = styled.div`
  margin-right: 10px;
  border-radius: 4px;
  max-width: 28px;
  max-height: 28px;
  > img {
    display: block;
  }
`;

const StyledHandleIcon = styled(HandleIcon)`
  margin-right: 8px;
  width: 24px;
  height: 24px;
  cursor: grab;
  z-index: 3;
  flex-shrink: 0;
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    margin-right: 0;
    opacity: 0;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    transition: opacity
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }
`;

const TrackLength = styled(Typography)`
  color: ${({ theme }) => theme.colors.tertiary};
`;

const ErrorWrapper = styled.div`
  padding: 10px 16px;
  margin-top: 3px;
  background-color: ${({ theme }) => theme.colors.accentLight};
`;

const UploadStatusText = styled(Typography)<{ status: string }>`
  font-size: 12px;
  margin-top: 8px;
  color: ${({ theme, status }) =>
    status === 'success'
      ? theme.colors.success
      : status === 'error'
      ? theme.colors.error
      : status === 'uploading'
      ? theme.colors.upload
      : theme.colors.primary};
  display: ${({ status }) => (status ? 'inline' : 'none')};
`;

const MenuButton = styled.button`
  background-color: ${({ theme }) => theme.colors.differentBackground};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryHover};
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;
